import { calcShapeErrorMessages } from 'pages/MainPage/PlanPage/shapeMeasurementsError';
import { createSelector } from 'reselect';
import { calcKitchenIslandErrorMessages } from 'pages/MainPage/KitchenIslandPage/kitchenIslandMeasurementsError';
import { shapeFieldsSelector } from 'domain/Options/selectors/input';
import { worktopWithOverhangMeasurementsSelector, kitchenIslandWithOverhangMeasurementsSelector } from 'domain/Options/selectors/overhangMeasurements';
import _ from 'lodash';
import { calcWallPanelErrorMessage } from 'pages/MainPage/StylePage/wallPanelMeasurementsError';
import { WALL_PANEL_ID, WALL_EDGING_STRIP_CUSTOMIZABLE_ID } from 'domain/Options/generators/splashbacks';
import { NO_OVERHANG_ID } from 'domain/Options/generators/overhangs';
import { KITCHEN_ISLAND_ID } from 'domain/Options/generators/kitchenIslands';
import { NO_WORKTOP_MATERIAL } from 'domain/Options/generators/materials';
import { freestandingHobsSelector } from 'domain/Options/selectors/operations';
import { configurationSelector } from 'domain/Options/selectors';
import { useImperialUnitMeasurementsSelector } from '../../../domain/Options/selectors';
import { wallPanelMinHeightSelector, wallPanelMaxHeightSelector, wallEdgingStripCustomizableMinHeightSelector, wallEdgingStripCustomizableMaxHeightSelector, measurementsLimitSelector, kitchenIslandMeasurementsLimitSelector } from './measurements';
import { plannedShapeParameterSelector, splashbackParameterSelector, wallEdgingStripCustomizableParameterSelector, kitchenIslandParameterSelector, sinkModelDepthConditionSelector, sinkFasteningParameterSelector, sinkSelector, overhangSideParameterSelector, overhangParameterSelector, kitchenIslandOverhangParameterSelector, kitchenIslandOverhangSideParameterSelector, materialParameterSelector } from './parameters';
import { fasteningTypes } from '../generators/sinks';

export const calcShapeErrorMessageSelector = createSelector(
  shapeFieldsSelector,
  measurementsLimitSelector,
  sinkModelDepthConditionSelector,
  sinkFasteningParameterSelector,
  sinkSelector,
  worktopWithOverhangMeasurementsSelector,
  overhangSideParameterSelector,
  plannedShapeParameterSelector,
  overhangParameterSelector,
  freestandingHobsSelector,
  configurationSelector,
  (shapeFields, limits, sinkDepthCond, sinkFastening, sink, getWorktopWithOverhangMeasurements, overhangSide, plannedShape, overhang, freestandingHob, configuration) => {
    let worktopWithOverhangMeasurements = null;
    if (overhang.getIn(['value', 'id']) !== NO_OVERHANG_ID) {
      worktopWithOverhangMeasurements = getWorktopWithOverhangMeasurements(overhangSide.get('value')).toJS();
    }
    const measurementWithoutOverhang = plannedShape.get('measurements');
    return (shape) => calcShapeErrorMessages(shape, limits, shapeFields[shape.id], plannedShape.getIn(['value', 'id']) === shape.id ? worktopWithOverhangMeasurements : null, sinkDepthCond, sink && sink.get('width'), sink && sinkFastening.getIn(['value', 'id']) === fasteningTypes.fitBetween, freestandingHob, configuration.get('freestandingHobDefaultWidth'), configuration.get('useImperialUnitMeasurementsInTheUI'), measurementWithoutOverhang);
  }
);

export const calcKitchenIslandErrorMessagesSelector = createSelector(
  kitchenIslandParameterSelector,
  kitchenIslandWithOverhangMeasurementsSelector,
  kitchenIslandMeasurementsLimitSelector,
  kitchenIslandOverhangSideParameterSelector,
  kitchenIslandOverhangParameterSelector,
  useImperialUnitMeasurementsSelector,
  (kitchenIsland, measurementsWithOverhang, limits, overhangSideParameter, overhang, useImperialUnits) => {
    if (kitchenIsland.getIn(['value', 'id']) !== KITCHEN_ISLAND_ID) {
      return () => [];
    }
    let measurements;
    let currMeasurementsWithOverHang = null;

    if (overhang.getIn(['value', 'id']) !== NO_OVERHANG_ID) {
      currMeasurementsWithOverHang = measurementsWithOverhang(overhangSideParameter.get('value')).toJS();
    }
    measurements = kitchenIsland.get('measurements').toJS();
    
    return (dimensions) => calcKitchenIslandErrorMessages(measurements, dimensions, limits, currMeasurementsWithOverHang, useImperialUnits);
  }
);

export const shapeInvalidSelector = createSelector(
  splashbackParameterSelector,
  materialParameterSelector,
  plannedShapeParameterSelector,
  calcShapeErrorMessageSelector,
  (splashback, materialParameter, plannedShape, calcShapeError) => {
    const shape = plannedShape.get('value') && plannedShape.get('value').toJS();
    const materialSelected = materialParameter.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;
    if (!shape) {
      return true;
    }
    if (!materialSelected && splashback.getIn(['value', 'id']) !== WALL_PANEL_ID) {
      return false;
    }
    return calcShapeError(shape).length > 0;
  }
);

export const kitchenIslandInvalidSelector = createSelector(
  calcKitchenIslandErrorMessagesSelector,
  kitchenIslandParameterSelector,
  (calcKitchenIslandError, kitchenIsland) => {
    if (kitchenIsland.getIn(['value', 'id']) !== KITCHEN_ISLAND_ID) {
      return false;
    }
    const dimensions = kitchenIsland.getIn(['value', 'dimensions']).toJS();
    return calcKitchenIslandError(dimensions).length > 0;
  }
);

export const shapeAndSinkInvalidSelector = createSelector(
  materialParameterSelector,
  plannedShapeParameterSelector,
  calcShapeErrorMessageSelector,
  (materialParameter, plannedShape, calcShapeError) => {
    const shape = plannedShape.get('value') && plannedShape.get('value').toJS();
    const materialSelected = materialParameter.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;
    if (!shape) {
      return true;
    }
    if (!materialSelected) {
      return false;
    }
    const errors = calcShapeError(shape);

    return errors.length > 0 && _.every(errors, 'showSchematicDrawing');
  }
);

export const wallPanelHeightInvalidSelector = createSelector(
  splashbackParameterSelector,
  wallPanelMinHeightSelector,
  wallPanelMaxHeightSelector,
  useImperialUnitMeasurementsSelector,
  (splashback, minHeight, maxHeight, usingImperial) => {
    if (!splashback) {
      return true;
    }
    if (splashback.getIn(['value', 'id']) !== WALL_PANEL_ID) {
      return false;
    }
    const currSplashBack = splashback.get('height');
    
    return calcWallPanelErrorMessage(currSplashBack, minHeight, maxHeight, usingImperial);
  }
);

export const wallEdgingStripCustomizableHeightInvalidSelector = createSelector(
  splashbackParameterSelector,
  wallEdgingStripCustomizableParameterSelector,
  wallEdgingStripCustomizableMinHeightSelector,
  wallEdgingStripCustomizableMaxHeightSelector,
  useImperialUnitMeasurementsSelector,
  (splashback, wallEdgingStripCustomizable, minHeight, maxHeight, usingImperial) => {
    if (!splashback) {
      return true;
    }
    if (splashback.getIn(['value', 'id']) !== WALL_EDGING_STRIP_CUSTOMIZABLE_ID) {
      return false;
    }
    const currSplashBack = wallEdgingStripCustomizable.get('height');
    return calcWallPanelErrorMessage(currSplashBack, minHeight, maxHeight);
  }
);
